import React, { useMemo, useState } from "react";
import { useTheme } from "styled-components";
import { FiSearch, FiX, FiChevronRight, FiGrid, FiList, FiUsers, FiFolder } from "react-icons/fi";
import {
  CardGrid, EntityCard, Group, GroupLabel, Row, AvatarStack, SearchField, Empty, Skeleton, Section, Pill, tokens,
} from "../styles";
import { Avatar, Sparkline, SegmentedControl } from "./primitives";
import { IOS, relativeTime, isOnline, moduleMeta, describe } from "../labels";

const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); };

export default function EntityList({ kind, items, loading, onOpen }) {
  const theme = useTheme();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [view, setView] = useState("cards");

  const isUser = kind === "user";
  const accent = isUser ? IOS.green : IOS.indigo;
  const otherLabel = (n) => (isUser ? (n === 1 ? "projeto" : "projetos") : (n === 1 ? "usuário" : "usuários"));

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = items.filter((i) => !term || `${i.name} ${i.subtitle}`.toLowerCase().includes(term));
    if (sort === "active") list = [...list].sort((a, b) => b.total - a.total);
    else if (sort === "az") list = [...list].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    else list = [...list].sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt));
    return list;
  }, [items, q, sort]);

  // Em "Recentes", agrupa como o iOS: Online agora · Hoje · Anteriormente
  const sections = useMemo(() => {
    if (sort !== "recent") return [{ key: "all", label: null, items: filtered }];
    const today = startOfToday();
    const s = [
      { key: "online", label: isUser ? "Online agora" : "Ativos agora", items: [] },
      { key: "today", label: "Hoje", items: [] },
      { key: "before", label: "Anteriormente", items: [] },
    ];
    filtered.forEach((i) => {
      const t = new Date(i.lastAt).getTime();
      if (isOnline(i.lastAt)) s[0].items.push(i);
      else if (t >= today) s[1].items.push(i);
      else s[2].items.push(i);
    });
    return s.filter((x) => x.items.length > 0);
  }, [filtered, sort, isUser]);

  const toolbar = (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <SearchField $max="420px">
        <FiSearch />
        <input placeholder={isUser ? "Buscar usuário ou e-mail" : "Buscar projeto ou CNPJ"} value={q} onChange={(e) => setQ(e.target.value)} />
        {q && <button className="clear" onClick={() => setQ("")}><FiX /></button>}
      </SearchField>
      <div style={{ flex: 1 }} />
      <SegmentedControl
        small
        value={sort}
        onChange={setSort}
        options={[{ key: "recent", label: "Recentes" }, { key: "active", label: "Mais ativos" }, { key: "az", label: "A–Z" }]}
      />
      <SegmentedControl
        small
        value={view}
        onChange={setView}
        options={[{ key: "cards", icon: <FiGrid />, label: "" }, { key: "list", icon: <FiList />, label: "" }]}
      />
    </div>
  );

  if (loading) {
    return (
      <Section $first>
        {toolbar}
        <CardGrid style={{ marginTop: 16 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <EntityCard key={i} $color={tokens.fill(theme)} style={{ cursor: "default" }}>
              <div className="head"><Skeleton $w="44px" $h={44} $r={22} /><div style={{ flex: 1 }}><Skeleton $w="70%" $h={13} /><div style={{ height: 6 }} /><Skeleton $w="45%" $h={10} /></div></div>
              <div style={{ height: 16 }} /><Skeleton $h={34} />
            </EntityCard>
          ))}
        </CardGrid>
      </Section>
    );
  }

  return (
    <Section $first>
      {toolbar}

      {filtered.length === 0 && (
        <div style={{ marginTop: 16 }}>
          <Group>
            <Empty>
              <div className="e-icon">{isUser ? <FiUsers /> : <FiFolder />}</div>
              <div className="e-title">{q ? "Nenhum resultado" : isUser ? "Nenhum usuário ativo" : "Nenhum projeto ativo"}</div>
              <div className="e-sub">{q ? `Nada encontrado para "${q}".` : "Ninguém usou o sistema neste período."}</div>
            </Empty>
          </Group>
        </div>
      )}

      {sections.map((sec, si) => (
        <div key={sec.key} style={{ marginTop: 18 }}>
          {sec.label && (
            <GroupLabel $first={si === 0} style={{ padding: "0 4px 9px" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {sec.key === "online" && <span style={{ width: 7, height: 7, borderRadius: "50%", background: IOS.green }} />}
                {sec.label}
              </span>
              <span>{sec.items.length}</span>
            </GroupLabel>
          )}

          {view === "cards" ? (
            <CardGrid>
              {sec.items.map((it, idx) => {
                const color = it.color || accent;
                const lastMeta = moduleMeta(it.lastActivity?.module);
                return (
                  <EntityCard key={it.id} $color={color} $delay={Math.min(idx, 12) * 30} onClick={() => onOpen(kind, it)}>
                    <div className="head">
                      <Avatar id={it.id} name={it.name} color={it.color} size={44} square={!isUser} online={isUser && isOnline(it.lastAt)} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="name">{it.name}</div>
                        <div className="sub">{it.subtitle || (it.cargo ? it.cargo : "—")}</div>
                      </div>
                      <Sparkline data={it.spark} color={color} width={72} height={26} />
                    </div>

                    <div className="stats">
                      <div><div className="s-value">{it.total}</div><div className="s-label">eventos</div></div>
                      <div><div className="s-value">{it.actions}</div><div className="s-label">ações</div></div>
                      <div>
                        <div className="s-value">{it.othersCount}</div>
                        <div className="s-label">{otherLabel(it.othersCount)}</div>
                      </div>
                    </div>

                    <div className="foot">
                      <AvatarStack>
                        {it.topOthers.slice(0, 4).map((o) => (
                          <Avatar key={o.id} id={o.id} name={o.name} size={22} square={isUser} title={`${o.name} · ${o.count}`} />
                        ))}
                        {it.othersCount > 4 && <span className="more">+{it.othersCount - 4}</span>}
                      </AvatarStack>
                      <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: lastMeta.color, flexShrink: 0 }} />
                        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {describe(it.lastActivity)} · {relativeTime(it.lastAt)}
                        </span>
                      </span>
                    </div>
                  </EntityCard>
                );
              })}
            </CardGrid>
          ) : (
            <Group>
              {sec.items.map((it) => (
                <Row key={it.id} $clickable $inset="68px" onClick={() => onOpen(kind, it)}>
                  <Avatar id={it.id} name={it.name} color={it.color} size={40} square={!isUser} online={isUser && isOnline(it.lastAt)} />
                  <div className="main">
                    <div className="title">
                      {it.name}
                      {it.cargo === "god" && <Pill $color={IOS.red}>god</Pill>}
                    </div>
                    <div className="subtitle">
                      {describe(it.lastActivity)} · {it.othersCount} {otherLabel(it.othersCount)}
                    </div>
                  </div>
                  <div className="trailing">
                    <Sparkline data={it.spark} color={it.color || accent} width={70} height={22} />
                    <div style={{ textAlign: "right", minWidth: 62 }}>
                      <div style={{ color: tokens.label(theme), fontWeight: 600 }}>{it.total}</div>
                      <div style={{ fontSize: "0.88em" }}>{relativeTime(it.lastAt)}</div>
                    </div>
                    <FiChevronRight className="chevron" />
                  </div>
                </Row>
              ))}
            </Group>
          )}
        </div>
      ))}
    </Section>
  );
}
